import { useState, useEffect, useRef } from "react";
import { createVacancy, updateVacancy, generateVacancyDescriptionAndSalary, postVacancyToHH, syncVacancyToHH, getVacancyTypes} from "../../services/vacancyApi";
import { DEPARTMENTS, GENDERS, UPDATE_DATES } from "../../config/api";
import {useAlertContext} from "../../context/AlertContext";
import {useHHDictionaries} from "../../hooks/useHHDictionaries";
import {mapVacancyToBackend, mapVacancyFromBackend} from "../../utils/vacancyMapper";
import AreaSelect from "../common/AreaSelect";
import RoleFilter from "../candidates/RoleFilter";
import "./VacancyForm.css";

export default function VacancyForm({ initialData, onClose, onVacancyAdded, publishToHH = null }) {
    const {dictionaries, loading: dictLoading} =  useHHDictionaries();
    const mappedInitial = initialData ? mapVacancyFromBackend(initialData) : null;
    const [generationMode, setGenerationMode] = useState(null);
    const generationLockRef = useRef(false);
    const [publishing, setPublishing] = useState(false);
    const [publishAfterSave, setPublishAfterSave] = useState(false);
    const [hhUrl, setHhUrl] = useState(initialData?.hh_vacancy_url || null);
    const [vacancyTypes, setVacancyTypes] = useState([]);
    // ID вакансии: берём из initialData (редактирование) или из только что созданной (после createVacancy)
    const [editingId, setEditingId] = useState(initialData?.id ?? null);

    const [formData, setFormData] = useState({
        name: initialData?.name || "",
        vacancy_type_id: mappedInitial?.vacancy_type_id || "",
        professional_role_id: mappedInitial?.professional_role_id || null,
        description: mappedInitial?.description || "",
        synonyms: Array.isArray(mappedInitial?.synonyms) ? mappedInitial.synonyms.join(", ") : "",
        department: mappedInitial?.department || DEPARTMENTS[0],
        area_id: String(mappedInitial?.area_id ?? initialData?.area_id ?? initialData?.area?.id ?? "") || "",
        work_format: mappedInitial?.work_format || mappedInitial?.schedule_id || "",
        employment_type: mappedInitial?.employment_type || mappedInitial?.employment_id || "",
        age: mappedInitial?.age || 18,
        gender: mappedInitial?.gender || GENDERS[0],
        languages: Array.isArray(mappedInitial?.languages) ? mappedInitial.languages.join(", ") : "",
        personal_characteristics: mappedInitial?.personal_characteristics || "",
        relevant_position_expirience: mappedInitial?.relevant_position_expirience || "",
        certain_position_expirience: mappedInitial?.certain_position_expirience || "",
        total_work_expirience: mappedInitial?.total_work_expirience || "",
        other_work_expirience: Array.isArray(mappedInitial?.other_work_expirience) ? mappedInitial.other_work_expirience.join(", ") : "",
        average_service_length: mappedInitial?.average_service_length || 0,
        education: Array.isArray(mappedInitial?.education) ? mappedInitial.education.join(", ") : "",
        required_hard_skills: Array.isArray(mappedInitial?.required_hard_skills) ? mappedInitial.required_hard_skills.join(", ") : "",
        optional_hard_skills: Array.isArray(mappedInitial?.optional_hard_skills) ? mappedInitial.optional_hard_skills.join(", ") : "",
        main_tasks: Array.isArray(mappedInitial?.main_tasks) ? mappedInitial.main_tasks.join(", ") : "",
        secondary_tasks: Array.isArray(mappedInitial?.secondary_tasks) ? mappedInitial.secondary_tasks.join(", ") : "",
        work_programs: Array.isArray(mappedInitial?.work_programs) ? mappedInitial.work_programs.join(", ") : "",
        kpi_metrics: Array.isArray(mappedInitial?.kpi_metrics) ? mappedInitial.kpi_metrics.join(", ") : "",
        resume_update_date: mappedInitial?.resume_update_date || UPDATE_DATES[0],
        active_search: mappedInitial?.active_search !== undefined ? mappedInitial.active_search : true,
        work_address: mappedInitial?.work_address || "",
        is_internal_hidden: Boolean(mappedInitial?.is_internal_hidden),
        salary_from: mappedInitial?.salary_from || 0,
        salary_to: mappedInitial?.salary_to || 0,
    });
    const { showAlert } = useAlertContext();

    const publishVacancy = async (vacancyId) => {
        const response = publishToHH
            ? await publishToHH(vacancyId)
            : await postVacancyToHH(vacancyId);
        const hhPayload = response?.hh_result || response || {};
        return {
            ...hhPayload,
            hh_vacancy_id: response?.hh_vacancy_id ?? hhPayload?.hh_vacancy_id ?? hhPayload?.id,
            hh_vacancy_url: response?.hh_vacancy_url ?? hhPayload?.hh_vacancy_url ?? hhPayload?.alternate_url,
        };
    };

    useEffect(() => {
        if (dictLoading) return;
        setFormData(prev => {
            const next = { ...prev };
            if (!next.work_format && dictionaries.schedules[0]?.id) {
                next.work_format = String(dictionaries.schedules[0].id);
            }
            if (!next.employment_type && dictionaries.employment[0]?.id) {
                next.employment_type = String(dictionaries.employment[0].id);
            }
            if (!next.total_work_expirience && dictionaries.experience[0]?.id) {
                next.total_work_expirience = String(dictionaries.experience[0].id);
            }
            return next;
        });
    }, [dictLoading, dictionaries]);

    useEffect(() => {
        (async () => {
            try {
                const list = await getVacancyTypes();
                const items = Array.isArray(list) ?  list : (list?.items || []);
                setVacancyTypes(items);
            } catch (e) {
                console.error("Не удалось загрузить типы вакансий", e)
                setVacancyTypes([])
            }
        })()
    }, []);

    useEffect(() => {
        if (!initialData) return;
        const incoming = mappedInitial?.area_id ?? initialData?.area_id ?? initialData?.area?.id;
        if (incoming !== undefined && incoming !== null && incoming !== "") {
            setFormData(prev => ({
                ...prev,
                area_id: String(incoming),
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData?.id]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const parseArrayFields = (data) => {
        const parseString = (str) => {
            if (typeof str === 'string') {
                return str.split(',').map(s => s.trim()).filter(Boolean);
            }
            return Array.isArray(str) ? str : [];
        };

        return {
            ...data,
            synonyms: parseString(data.synonyms),
            languages: parseString(data.languages),
            other_work_expirience: parseString(data.other_work_expirience),
            education: parseString(data.education),
            required_hard_skills: parseString(data.required_hard_skills),
            optional_hard_skills: parseString(data.optional_hard_skills),
            main_tasks: parseString(data.main_tasks),
            secondary_tasks: parseString(data.secondary_tasks),
            work_programs: parseString(data.work_programs),
            kpi_metrics: parseString(data.kpi_metrics),
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if ((formData.description || "").trim().length < 200) {
                showAlert("Описание вакансии должно быть не короче 200 символов (требование HH.ru)", "error");
                return;
            }
            if (!formData.professional_role_id) {
                showAlert("Укажите профессиональную роль", "error");
                return;
            }

            const parsedData = parseArrayFields(formData);
            const mappedData = mapVacancyToBackend(parsedData);
            if (mappedData.area_id !== undefined && mappedData.area_id !== null) {
                mappedData.area_id = String(mappedData.area_id);
            }

            const cleanData = {...mappedData};
            delete cleanData.id;
            delete cleanData.created_at;
            delete cleanData.updated_at;

            if (Array.isArray(cleanData.professional_roles_id)) {
                const filtered = cleanData.professional_roles_id.filter(id => id && id !== "" && id !== "0");
                if (filtered.length === 0) {
                    delete cleanData.professional_roles_id;
                } else {
                    cleanData.professional_roles_id = filtered;
                }
            }

            if(editingId) {
                Object.keys(cleanData).forEach(key => {
                    if(cleanData[key] === null || cleanData[key] === undefined || cleanData[key] === "") {
                        delete cleanData[key];
                    }
                })
            }

            let result;
            const alreadyOnHH = Boolean(
                hhUrl
                || initialData?.hh_vacancy_id
                || initialData?.hh_vacancy_url
            );
            if (editingId) {
                // PATCH on database-service auto-syncs to HH.ru when hh_vacancy_id is set
                result = await updateVacancy(editingId, cleanData);
            } else {
                result = await createVacancy(cleanData);
                if (result?.id) {
                    setEditingId(result.id);
                }
            }
            const savedId = result?.id || editingId;
            const savedArea = result?.area_id ?? result?.area?.id ?? formData.area_id;
            if (savedArea !== undefined && savedArea !== null && savedArea !== "") {
                const savedAreaStr = String(savedArea);
                setFormData(prev => ({ ...prev, area_id: savedAreaStr }));
                result = {
                    ...result,
                    area_id: savedAreaStr,
                    area: { ...(result?.area || {}), id: Number(savedAreaStr) }
                };
            }
            if (result?.hh_vacancy_url) setHhUrl(result.hh_vacancy_url);

            let finalResult = result;

            if (result?._hhSyncError) {
                showAlert(`Сохранено локально, но HH.ru не обновлён: ${result._hhSyncError}`, "warning");
            } else if (result?._hhSynced) {
                showAlert("Вакансия сохранена и обновлена на HH.ru", "success");
            } else {
                showAlert("Вакансия сохранена", "success");
            }

            if (publishAfterSave && savedId && !result?._hhSynced) {
                try {
                    setPublishing(true);
                    if ((alreadyOnHH || result?.hh_vacancy_id) && !publishToHH) {
                        showAlert("Обновляем на HH.ru...", "info");
                        const synced = await syncVacancyToHH(savedId);
                        const url = synced?.alternate_url || synced?.hh_vacancy_url || hhUrl;
                        if (url) setHhUrl(url);
                        finalResult = {
                            ...result,
                            hh_vacancy_id: synced?.hh_vacancy_id ?? result?.hh_vacancy_id,
                            hh_vacancy_url: url,
                            _hhSynced: true,
                        };
                        showAlert("Вакансия обновлена на HH.ru", "success");
                    } else {
                        showAlert("Публикуем на HH.ru...", "info");
                        const published = await publishVacancy(savedId);
                        const url = published?.alternate_url || published?.hh_vacancy_url || null;
                        if (url) setHhUrl(url);
                        finalResult = {
                            ...result,
                            hh_vacancy_id: published?.hh_vacancy_id,
                            hh_vacancy_url: url,
                            _hhSynced: true,
                        };
                        showAlert("Вакансия опубликована на HH.ru", "success");
                    }
                } catch (pubErr) {
                    showAlert(`Сохранено локально, но публикация на HH не удалась: ${pubErr.message}`, "error");
                } finally {
                    setPublishing(false);
                }
            }

            // Close / refresh parent only after optional HH publish finishes
            onVacancyAdded(finalResult);
        } catch (err) {
            console.error("Ошибка сохранения:", err);
            showAlert(`Ошибка: ${err.message}`, "error")
        }
    };


    const validateRequiredFields = () => {
        const required = {
            "Должность": formData.name,
            "Тип вакансии": formData.vacancy_type_id,
            "Профессиональная роль": formData.professional_role_id,
            "Отдел": formData.department,
            "График работы": formData.work_format,
            "Местоположение": formData.area_id,
            "Тип занятости": formData.employment_type,
            "Возраст": formData.age,
            "Пол": formData.gender,
            "Общий опыт работы": formData.total_work_expirience,
            "Дата обновления резюме": formData.resume_update_date,
        }

        const missing = []
        for(const [field, value] of Object.entries(required)) {
            if(!value || value === "") {
                missing.push(field)
            }
        }

        return missing
    }

    const handleGenerate = async (mode) => {
        if (generationLockRef.current) return;
        const missingFields = validateRequiredFields();
        if (missingFields.length > 0) {
            showAlert(`Заполните обязательные поля: ${missingFields.join(', ')}`, "error");
            return;
        }

        try {
            generationLockRef.current = true;
            setGenerationMode(mode);
            showAlert("Сохраняем вакансию...", "info");

            const parsedData = parseArrayFields(formData);
            const mappedData = mapVacancyToBackend(parsedData);
            const cleanData = { ...mappedData };
            delete cleanData.id;
            delete cleanData.created_at;
            delete cleanData.updated_at;
            delete cleanData.description;
            delete cleanData.salary_from;
            delete cleanData.salary_to;

            const savedVacancy = editingId
                ? await updateVacancy(editingId, cleanData)
                : await createVacancy(cleanData);

            const vacancyId = savedVacancy?.id || editingId;
            if (!vacancyId) {
                throw new Error("Не удалось получить идентификатор вакансии");
            }
            if (savedVacancy?.id && !editingId) {
                setEditingId(savedVacancy.id);
            }

            showAlert(
                mode === "salary"
                    ? "Рассчитываем зарплатный диапазон по рынку..."
                    : "Формируем описание и зарплатный диапазон...",
                "info",
            );
            const result = await generateVacancyDescriptionAndSalary(vacancyId);
            const salaryPatch = {};
            if (result.salary_from !== null && result.salary_from !== undefined) {
                salaryPatch.salary_from = Math.round(result.salary_from);
            }
            if (result.salary_to !== null && result.salary_to !== undefined) {
                salaryPatch.salary_to = Math.round(result.salary_to);
            }
            if (!Object.keys(salaryPatch).length) {
                throw new Error("Не удалось определить зарплатный диапазон. Уточните должность, город и требования");
            }
            setFormData(prev => ({ ...prev, ...salaryPatch }));

            if (mode === "salary") {
                await updateVacancy(vacancyId, salaryPatch);
                showAlert("Зарплатный диапазон рассчитан и сохранён", "success");
                return;
            }

            const description = result.description || "";
            setFormData(prev => ({ ...prev, description }));
            const generatedPatch = { ...salaryPatch, description };
            if (description.trim().length >= 200) {
                await updateVacancy(vacancyId, generatedPatch);
                showAlert("Описание и зарплатный диапазон сохранены", "success");
            } else {
                await updateVacancy(vacancyId, salaryPatch);
                showAlert("Зарплата сохранена. Дополните описание минимум до 200 символов", "warning");
            }

        } catch (err) {
            showAlert(`Ошибка: ${err.message}`, "error");
        } finally {
            generationLockRef.current = false;
            setGenerationMode(null);
        }
    };

    const handleGenerateDescriptionAndSalary = () => handleGenerate("full");
    const handleGenerateMarketSalary = () => handleGenerate("salary");

    const handlePublishToHH = async () => {
        try {
            setPublishing(true);

            if ((formData.description || "").trim().length < 200) {
                showAlert("Описание должно быть не короче 200 символов перед публикацией на HH.ru", "error");
                return;
            }

            showAlert("Сохраняем вакансию перед публикацией...", "info");
            const parsedData = parseArrayFields(formData);
            const mappedData = mapVacancyToBackend(parsedData);
            if (mappedData.area_id !== undefined && mappedData.area_id !== null) {
                mappedData.area_id = String(mappedData.area_id);
            }
            const cleanData = { ...mappedData };
            delete cleanData.id;
            delete cleanData.created_at;
            delete cleanData.updated_at;

            const saved = editingId
                ? await updateVacancy(editingId, cleanData)
                : await createVacancy(cleanData);
            const vacancyId = saved?.id || editingId;
            if (saved?.id) setEditingId(saved.id);
            showAlert("Публикуем на HH.ru...", "info");
            const published = await publishVacancy(vacancyId);
            const url = published?.alternate_url || published?.hh_vacancy_url || null;
            if (url) setHhUrl(url);
            onVacancyAdded?.({
                ...saved,
                hh_vacancy_id: published?.hh_vacancy_id,
                hh_vacancy_url: url,
            });
            showAlert(
                (hhUrl || saved?.hh_vacancy_id)
                    ? "Вакансия обновлена на HH.ru!"
                    : "Вакансия успешно опубликована на HH.ru!",
                "success"
            );
        } catch (err) {
            showAlert(`Ошибка публикации: ${err.message}`, "error");
        } finally {
            setPublishing(false);
        }
    };

    const inputClass = "vacancy-field";
    const descriptionLength = (formData.description || "").length;
    const isGenerating = generationMode !== null;

    return (
        <form onSubmit={handleSubmit} className="vacancy-form">
            <header className="vacancy-form-header">
                <div><span>{editingId ? "РЕДАКТИРОВАНИЕ" : "НОВАЯ ВАКАНСИЯ"}</span><h2>{editingId ? "Редактировать вакансию" : "Создать вакансию"}</h2><p>Заполните профиль позиции — данные можно сохранить локально или сразу опубликовать на HH.ru.</p></div>
                <button type="button" onClick={onClose} className="vacancy-close" aria-label="Закрыть">×</button>
            </header>

            <div className="vacancy-form-content">
                <section className="vacancy-section">
                    <div className="vacancy-section-heading"><span>01</span><div><h3>Основная информация</h3><p>Название, роль и принадлежность вакансии.</p></div></div>
                    <div className="vacancy-grid vacancy-grid--2">
                        <label className="vacancy-field-wrap vacancy-span-2"><span>Должность <b>*</b></span><input type="text" value={formData.name} onChange={e => handleChange("name", e.target.value)} className={inputClass} placeholder="Например, менеджер по логистике" required/></label>
                        <label className="vacancy-field-wrap"><span>Профессиональная роль <b>*</b></span><RoleFilter value={formData.professional_role_id} onChange={(val) => handleChange("professional_role_id", val ? Number(val) : null)}/><small>Классификация HH.ru</small></label>
                        <label className="vacancy-field-wrap"><span>Тип вакансии <b>*</b></span><select value={String(formData.vacancy_type_id ?? "")} onChange={e => handleChange("vacancy_type_id", e.target.value)} className={inputClass}><option value="">Выберите тип</option>{vacancyTypes.filter(t => t && t.id !== undefined && t.id !== null).map(t => <option key={t.id} value={String(t.id)}>{t.name || t.title || t.label || `Тип #${t.id}`}</option>)}</select></label>
                        <label className="vacancy-field-wrap"><span>Отдел <b>*</b></span><select value={formData.department} onChange={e => handleChange("department", e.target.value)} className={inputClass} required>{DEPARTMENTS.map(dept => <option key={dept} value={dept}>{dept}</option>)}</select></label>
                        <label className="vacancy-field-wrap"><span>Синонимы должности</span><input type="text" value={formData.synonyms} onChange={e => handleChange("synonyms", e.target.value)} className={inputClass} placeholder="Менеджер, специалист"/><small>Укажите через запятую</small></label>
                    </div>
                </section>

                <section className="vacancy-section">
                    <div className="vacancy-section-heading"><span>02</span><div><h3>Условия работы</h3><p>График, занятость, расположение и доход.</p></div></div>
                    <div className="vacancy-grid vacancy-grid--3">
                        <label className="vacancy-field-wrap"><span>График работы <b>*</b></span><select value={formData.work_format} onChange={e => handleChange("work_format", e.target.value)} className={inputClass} required disabled={dictLoading}><option value="">Выберите график</option>{dictionaries.schedules.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
                        <label className="vacancy-field-wrap"><span>Тип занятости <b>*</b></span><select value={formData.employment_type} onChange={e => handleChange("employment_type", e.target.value)} className={inputClass} required disabled={dictLoading}><option value="">Выберите тип</option>{dictionaries.employment.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
                        <label className="vacancy-field-wrap"><span>Актуальность резюме <b>*</b></span><select value={formData.resume_update_date} onChange={e => handleChange("resume_update_date", e.target.value)} className={inputClass} required>{UPDATE_DATES.map(date => <option key={date} value={date}>{date}</option>)}</select></label>
                        <label className="vacancy-field-wrap"><span>Зарплата от, ₽</span><input type="number" value={formData.salary_from} onChange={e => handleChange("salary_from", parseInt(e.target.value) || 0)} className={inputClass} min="0"/></label>
                        <label className="vacancy-field-wrap"><span>Зарплата до, ₽</span><input type="number" value={formData.salary_to} onChange={e => handleChange("salary_to", parseInt(e.target.value) || 0)} className={inputClass} min="0"/></label>
                        <label className="vacancy-field-wrap"><span>Адрес рабочего места</span><input type="text" value={formData.work_address} onChange={e => handleChange("work_address", e.target.value)} className={inputClass} placeholder="Улица, дом, офис"/></label>
                        <div className="vacancy-field-wrap vacancy-span-3"><span>Местоположение <b>*</b></span><div className="vacancy-area"><AreaSelect value={formData.area_id || ""} onChange={(areaId) => { if (areaId !== null && areaId !== undefined && areaId !== "" && !Number.isNaN(Number(areaId))) handleChange("area_id", String(areaId)); }} required/></div></div>
                        <div className="vacancy-market-card vacancy-span-3">
                            <div className="vacancy-market-icon" aria-hidden="true">₽</div>
                            <div><strong>Подобрать зарплату по рынку</strong><p>Расчёт учитывает должность, профессиональную роль, город, формат работы и требуемый опыт. Полученный диапазон заполнит поля «от» и «до».</p></div>
                            <button type="button" onClick={handleGenerateMarketSalary} disabled={isGenerating || publishing}>{generationMode === "salary" ? "Рассчитываем…" : "Рассчитать зарплату"}</button>
                        </div>
                    </div>
                </section>

                <section className="vacancy-section">
                    <div className="vacancy-section-heading"><span>03</span><div><h3>Портрет кандидата</h3><p>Требования к опыту, образованию и личным качествам.</p></div></div>
                    <div className="vacancy-grid vacancy-grid--3">
                        <label className="vacancy-field-wrap"><span>Общий опыт <b>*</b></span><select value={formData.total_work_expirience} onChange={e => handleChange("total_work_expirience", e.target.value)} className={inputClass} required disabled={dictLoading}><option value="">Выберите опыт</option>{dictionaries.experience.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
                        <label className="vacancy-field-wrap"><span>Возраст <b>*</b></span><input type="number" value={formData.age} onChange={e => handleChange("age", parseInt(e.target.value) || 18)} className={inputClass} required min="18"/></label>
                        <label className="vacancy-field-wrap"><span>Пол <b>*</b></span><select value={formData.gender} onChange={e => handleChange("gender", e.target.value)} className={inputClass} required>{GENDERS.map(gender => <option key={gender} value={gender}>{gender}</option>)}</select></label>
                        <label className="vacancy-field-wrap"><span>Релевантный опыт</span><input type="text" value={formData.relevant_position_expirience} onChange={e => handleChange("relevant_position_expirience", e.target.value)} className={inputClass} placeholder="Например, от 2 лет"/></label>
                        <label className="vacancy-field-wrap"><span>Опыт на должности</span><input type="text" value={formData.certain_position_expirience} onChange={e => handleChange("certain_position_expirience", e.target.value)} className={inputClass}/></label>
                        <label className="vacancy-field-wrap"><span>Средний стаж, лет</span><input type="number" value={formData.average_service_length} onChange={e => handleChange("average_service_length", parseFloat(e.target.value) || 0)} className={inputClass} step="0.1" min="0"/></label>
                        <label className="vacancy-field-wrap"><span>Языки</span><input type="text" value={formData.languages} onChange={e => handleChange("languages", e.target.value)} className={inputClass} placeholder="Русский, английский"/><small>Укажите через запятую</small></label>
                        <label className="vacancy-field-wrap"><span>Образование</span><input type="text" value={formData.education} onChange={e => handleChange("education", e.target.value)} className={inputClass} placeholder="Высшее, среднее специальное"/></label>
                        <label className="vacancy-field-wrap"><span>Другой опыт</span><input type="text" value={formData.other_work_expirience} onChange={e => handleChange("other_work_expirience", e.target.value)} className={inputClass}/></label>
                        <label className="vacancy-field-wrap vacancy-span-3"><span>Личные характеристики</span><textarea value={formData.personal_characteristics} onChange={e => handleChange("personal_characteristics", e.target.value)} className={`${inputClass} vacancy-textarea`} placeholder="Какие качества помогут успешно работать на этой позиции?"/></label>
                    </div>
                </section>

                <section className="vacancy-section">
                    <div className="vacancy-section-heading"><span>04</span><div><h3>Задачи и компетенции</h3><p>Что предстоит делать и какие навыки необходимы.</p></div></div>
                    <div className="vacancy-grid vacancy-grid--2">
                        <label className="vacancy-field-wrap"><span>Обязательные навыки</span><textarea value={formData.required_hard_skills} onChange={e => handleChange("required_hard_skills", e.target.value)} className={`${inputClass} vacancy-textarea`} placeholder="Навык 1, навык 2"/></label>
                        <label className="vacancy-field-wrap"><span>Желательные навыки</span><textarea value={formData.optional_hard_skills} onChange={e => handleChange("optional_hard_skills", e.target.value)} className={`${inputClass} vacancy-textarea`} placeholder="Будет преимуществом…"/></label>
                        <label className="vacancy-field-wrap"><span>Основные задачи</span><textarea value={formData.main_tasks} onChange={e => handleChange("main_tasks", e.target.value)} className={`${inputClass} vacancy-textarea`} placeholder="Главные обязанности"/></label>
                        <label className="vacancy-field-wrap"><span>Второстепенные задачи</span><textarea value={formData.secondary_tasks} onChange={e => handleChange("secondary_tasks", e.target.value)} className={`${inputClass} vacancy-textarea`} placeholder="Дополнительные обязанности"/></label>
                        <label className="vacancy-field-wrap"><span>Программы для работы</span><input type="text" value={formData.work_programs} onChange={e => handleChange("work_programs", e.target.value)} className={inputClass} placeholder="Excel, 1C, CRM"/></label>
                        <label className="vacancy-field-wrap"><span>KPI и метрики</span><input type="text" value={formData.kpi_metrics} onChange={e => handleChange("kpi_metrics", e.target.value)} className={inputClass} placeholder="Срок, объём, качество"/></label>
                    </div>
                </section>

                <section className="vacancy-section vacancy-section--description">
                    <div className="vacancy-section-heading"><span>05</span><div><h3>Описание вакансии</h3><p>Финальный текст для кандидатов и публикации на HH.ru.</p></div></div>
                    <div className="vacancy-ai-card"><div className="vacancy-ai-icon">✦</div><div><strong>Заполнить описание и зарплату</strong><p>Помощник сформирует текст вакансии и одновременно рассчитает рыночный зарплатный диапазон по заполненным условиям.</p></div><button type="button" onClick={handleGenerateDescriptionAndSalary} disabled={isGenerating || publishing}>{generationMode === "full" ? "Формируем…" : "Создать описание и зарплату"}</button></div>
                    <label className="vacancy-field-wrap"><span>Текст вакансии <b>*</b></span><textarea value={formData.description} onChange={e => handleChange("description", e.target.value)} className={`${inputClass} vacancy-description`} required minLength="200" placeholder="Расскажите о компании, задачах, требованиях и условиях…"/><div className="vacancy-counter"><span className={descriptionLength >= 200 ? "is-ready" : ""}>{descriptionLength}/200 символов</span><small>{descriptionLength >= 200 ? "Описание готово к публикации" : `Добавьте ещё ${200 - descriptionLength}`}</small></div></label>
                </section>

                <section className="vacancy-section">
                    <div className="vacancy-section-heading"><span>06</span><div><h3>Настройки публикации</h3><p>Видимость вакансии и синхронизация с HH.ru.</p></div></div>
                    <div className="vacancy-toggle-list">
                        <label className="vacancy-toggle"><input type="checkbox" checked={formData.active_search} onChange={e => handleChange("active_search", e.target.checked)} id="active_search"/><span/><div><strong>Активный поиск</strong><small>Вакансия участвует в подборе кандидатов</small></div></label>
                        <label className="vacancy-toggle"><input type="checkbox" id="is_internal_hidden" checked={formData.is_internal_hidden} onChange={e => handleChange("is_internal_hidden", e.target.checked)}/><span/><div><strong>Скрыть во внутреннем рабочем списке</strong><small>Публикация и отклики HH продолжат работать</small></div></label>
                        <label className="vacancy-toggle"><input type="checkbox" checked={publishAfterSave} onChange={e => setPublishAfterSave(e.target.checked)} id="publish_after_save"/><span/><div><strong>{(hhUrl || initialData?.hh_vacancy_id || initialData?.hh_vacancy_url) ? "После сохранения обновить на HH.ru" : "После сохранения опубликовать на HH.ru"}</strong><small>Выполнится автоматически после сохранения формы</small></div></label>
                    </div>
                    {hhUrl && <div className="vacancy-published"><span>✓</span><div><strong>Вакансия опубликована на HH.ru</strong><a href={hhUrl} target="_blank" rel="noreferrer">Открыть публикацию</a></div></div>}
                </section>
            </div>

            <footer className="vacancy-form-footer">
                <div className="vacancy-footer-status"><span className={descriptionLength >= 200 ? "is-complete" : ""}/><p>{descriptionLength >= 200 ? "Описание готово" : "Нужно описание от 200 символов"}</p></div>
                <div className="vacancy-footer-actions">
                    <button type="button" onClick={onClose} className="vacancy-btn vacancy-btn--secondary">Отмена</button>
                    <button type="submit" className="vacancy-btn vacancy-btn--dark" disabled={publishing || isGenerating}>Сохранить</button>
                    <button type="button" onClick={handlePublishToHH} disabled={publishing || isGenerating} className="vacancy-btn vacancy-btn--hh">{publishing ? "Публикация..." : (hhUrl || initialData?.hh_vacancy_id ? "Обновить на HH.ru" : "Опубликовать на HH.ru")}</button>
                </div>
            </footer>
        </form>
    );
}
